import { useEffect, useState } from "react";
import { GraduationCap, FileText, Download, Loader2, ShieldCheck, Calendar as CalendarBlank, Award, Upload, CheckCircle2, Circle, ExternalLink } from "lucide-react";

/**
 * MyCursosDiplomas
 *
 * Página onde o cliente vê os cursos em que está inscrito, com dois
 * documentos por inscrição:
 *   - Diploma IHTP (gerado automaticamente após 100 % de presenças + docs)
 *   - Diploma DGERT (upload feito pelo admin, quando aplicável)
 *
 * API:
 *   GET /api/my/cursos-diplomas → { total, cursos: [...] }
 */

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

function DiplomaBadge({ label, url, subtitle, tone }) {
  const disabled = !url;
  const toneCls =
    tone === "gold"
      ? "border-[#C6A15A] bg-[#FBF7EE] text-[#7A5B14] group-hover:bg-[#0F1829] group-hover:border-[#0F1829] group-hover:text-white"
      : "border-[#3D6E52] bg-[#EEF6F0] text-[#254C36] group-hover:bg-[#0F1829] group-hover:border-[#0F1829] group-hover:text-white";
  return (
    <a
      href={url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => disabled && e.preventDefault()}
      data-testid={`diploma-link-${tone}`}
      className={`group flex items-center gap-3 rounded-lg border p-3 transition ${
        disabled
          ? "border-[#E6E1D6] bg-[#FAF8F2] text-[#B8B0A0] cursor-not-allowed"
          : `${toneCls} cursor-pointer`
      }`}
    >
      <div
        className={`w-9 h-9 rounded grid place-items-center shrink-0 ${
          disabled
            ? "bg-white text-[#C7BFA8]"
            : "bg-white/70"
        }`}
      >
        {tone === "gold" ? <Award size={18} /> : <ShieldCheck size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">{label}</div>
        <div className="text-[11px] mt-0.5 opacity-75">
          {disabled ? "Ainda não disponível" : subtitle || "Descarregar PDF"}
        </div>
      </div>
      {!disabled && <Download size={14} className="shrink-0" />}
    </a>
  );
}


/**
 * CertificacaoDocumentos — mostra estado dos docs exigidos ao formando
 * para as certificações Practitioner/Master de PNL e Coaching. O formando
 * pode descarregar templates (License Agreement) e fazer upload dos
 * documentos assinados/entregues.
 *
 * Se o curso não tiver certificação especial, este componente devolve null.
 */
function CertificacaoDocumentos({ fichaId }) {
  const [state, setState] = useState({ loading: true, docs: [], tipo: null });
  const [uploading, setUploading] = useState({});

  const load = async () => {
    try {
      const r = await fetch(`/api/my/cursos/${fichaId}/documentos`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setState({
        loading: false,
        docs: j.docs || [],
        tipo: j.ficha?.TipoCertificacao || null,
      });
    } catch (e) {
      setState({ loading: false, docs: [], tipo: null, error: e.message });
    }
  };
  useEffect(() => { load(); }, [fichaId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (tipo, file) => {
    setUploading((u) => ({ ...u, [tipo]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`/api/my/cursos/${fichaId}/documentos/${tipo}`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Erro upload");
      await load();
    } catch (e) {
      alert(e.message || "Erro no upload");
    } finally {
      setUploading((u) => ({ ...u, [tipo]: false }));
    }
  };

  if (state.loading) return null;
  if (!state.tipo || state.docs.length === 0) return null;

  const done = state.docs.filter((d) => d.uploaded).length;
  return (
    <div
      className="border-t border-[#F0EBDF] px-4 sm:px-5 py-4 bg-[#FBF9F3]"
      data-testid={`cert-docs-${fichaId}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={14} className="text-[#A8843C]" />
        <h3 className="text-xs uppercase tracking-widest text-[#7A5B14] font-bold">
          Documentos da Certificação
        </h3>
        <span className="ml-auto text-[11px] text-[#978D78]">
          {done}/{state.docs.length} entregues
        </span>
      </div>
      <div className="space-y-2">
        {state.docs.map((d) => {
          const isUp = !!uploading[d.tipo];
          return (
            <div
              key={d.tipo}
              data-testid={`cert-doc-${fichaId}-${d.tipo}`}
              className="flex items-start gap-2 bg-white border border-[#E6E1D6] rounded px-3 py-2"
            >
              {d.uploaded ? (
                <CheckCircle2 size={16} className="text-green-600 mt-0.5 shrink-0" />
              ) : (
                <Circle size={16} className="text-[#C7BFA8] mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1B1B1B]">{d.label}</div>
                {d.hint && !d.uploaded && (
                  <div className="text-[11px] text-[#6B7280] mt-0.5">{d.hint}</div>
                )}
                {d.uploaded && d.data_upload && (
                  <div className="text-[11px] text-[#978D78] mt-0.5">
                    Entregue a {fmtDate(d.data_upload)}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                {d.template_url && (
                  <a
                    href={d.template_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`cert-template-${fichaId}-${d.tipo}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#0F1829] text-[#0F1829] hover:bg-[#0F1829] hover:text-white transition"
                  >
                    <ExternalLink size={11} /> Template
                  </a>
                )}
                {d.uploaded && d.url && (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`cert-view-${fichaId}-${d.tipo}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-green-600 text-green-700"
                  >
                    Ver
                  </a>
                )}
                <label
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border cursor-pointer transition ${
                    isUp
                      ? "border-[#E6E1D6] bg-[#F5F2EB] text-[#B8B0A0] cursor-not-allowed"
                      : "border-[#A8843C] text-[#A8843C] hover:bg-[#A8843C] hover:text-white"
                  }`}
                  data-testid={`cert-upload-${fichaId}-${d.tipo}`}
                >
                  {isUp ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                  {d.uploaded ? "Substituir" : "Upload"}
                  <input
                    type="file"
                    accept=".pdf,.docx,.zip,.jpg,.png"
                    className="hidden"
                    disabled={isUp}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload(d.tipo, f);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyCursosDiplomas() {
  const [loading, setLoading] = useState(true);
  const [cursos, setCursos] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/my/cursos-diplomas", { credentials: "include" });
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        const j = await r.json();
        setCursos(j.cursos || []);
      } catch (e) {
        setError(e.message || "Erro ao carregar cursos.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl" data-testid="my-cursos-loading">
        <h1
          className="text-2xl text-[#0F1829] mb-4"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
        >
          Cursos e Diplomas
        </h1>
        <div className="flex items-center gap-2 text-[#978D78] text-sm">
          <Loader2 size={14} className="animate-spin" /> A carregar…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl" data-testid="my-cursos-error">
        <div className="rounded-xl bg-red-50 border border-red-200 p-6">
          <h2 className="text-lg font-semibold text-red-900" style={{ fontFamily: "'Fraunces', serif" }}>
            Não foi possível carregar
          </h2>
          <p className="text-sm text-red-800 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="my-cursos-page">
      <div className="mb-6 flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-[#0F1829] grid place-items-center text-white shrink-0">
          <GraduationCap size={22} />
        </div>
        <div>
          <h1 className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>
            Cursos e Diplomas
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Cursos em que participou e diplomas emitidos pela IHTP e DGERT.
            {cursos.length > 0 && (
              <span className="ml-2 text-[#978D78]">
                ({cursos.length} curso{cursos.length !== 1 ? "s" : ""})
              </span>
            )}
          </p>
        </div>
      </div>

      {cursos.length === 0 ? (
        <div
          className="rounded-xl border border-[#E6E1D6] bg-white p-10 text-center"
          data-testid="my-cursos-empty"
        >
          <div className="w-14 h-14 rounded-full bg-[#F5F2EB] grid place-items-center text-[#978D78] mx-auto mb-4">
            <FileText size={22} />
          </div>
          <h2 className="text-lg text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif" }}>
            Sem cursos registados
          </h2>
          <p className="text-sm text-[#6B7280]">
            Ainda não está inscrito em nenhum curso IHTP. Quando frequentar
            um curso, os diplomas aparecerão aqui.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {cursos.map((c) => (
            <article
              key={c.ID_FichaInscricao}
              data-testid={`my-curso-${c.ID_FichaInscricao}`}
              className="rounded-xl border border-[#E6E1D6] bg-white overflow-hidden"
            >
              <header className="p-4 sm:p-5 border-b border-[#F0EBDF] flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h2
                    className="text-base sm:text-lg text-[#0F1829] leading-snug"
                    style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
                  >
                    {c.NomeCurso}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-[#978D78]">
                    {c.Edicao && (
                      <span className="uppercase tracking-wider">Edição · {c.Edicao}</span>
                    )}
                    {c.CursoNoSigo && (
                      <span className="uppercase tracking-wider">SIGO · {c.CursoNoSigo}</span>
                    )}
                    {c.DataInicio && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarBlank size={11} />
                        {fmtDate(c.DataInicio)}
                        {c.DataFim && c.DataFim !== c.DataInicio && ` → ${fmtDate(c.DataFim)}`}
                      </span>
                    )}
                  </div>
                </div>
                {c.Nota != null && (
                  <div
                    className="text-center rounded-lg bg-[#0F1829] text-white px-3 py-2 shrink-0"
                    title="Nota final"
                  >
                    <div className="text-[9px] uppercase tracking-widest opacity-70">Nota</div>
                    <div className="text-lg font-semibold leading-none mt-0.5">
                      {c.Nota.toFixed(1)}
                    </div>
                  </div>
                )}
              </header>

              <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <DiplomaBadge
                  tone="gold"
                  label="Diploma IHTP"
                  url={c.diploma_ihtp?.download_url || null}
                  subtitle={c.diploma_ihtp?.enviado_em ? `Emitido a ${fmtDate(c.diploma_ihtp.enviado_em)}` : null}
                />
                <DiplomaBadge
                  tone="green"
                  label="Diploma DGERT"
                  url={c.diploma_dgert?.download_url || null}
                  subtitle={c.diploma_dgert?.uploaded_em ? `Disponibilizado a ${fmtDate(c.diploma_dgert.uploaded_em)}` : null}
                />
              </div>

              <CertificacaoDocumentos fichaId={c.ID_FichaInscricao} />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
